import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/components/providers/supabase-auth-provider'
import { SUBSCRIPTION_TIERS, SubscriptionTier, TRIAL_CONFIG } from '@/config/subscriptions'
import { Subscription } from '@/lib/supabase'

type StripeDetails = {
  nextBillingDate: number | null; // Unix timestamp
  paymentMethodDetails: {
    brand: string | null;
    last4: string | null;
  } | null;
}

type TrialInfo = {
  isTrialing: boolean;
  daysRemaining: number;
  trialEndsAt: string | null;
  aiRequestsUsed: number;
  aiRequestsLimit: number;
}

export function useSubscription() {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [stripeDetails, setStripeDetails] = useState<StripeDetails | null>(null)
  const [tier, setTier] = useState<SubscriptionTier>(SUBSCRIPTION_TIERS.free as SubscriptionTier)
  const [isLoading, setIsLoading] = useState(true)
  const [isDetailsLoading, setIsDetailsLoading] = useState(false)
  const { user, session } = useAuth()

  const sessionRef = useRef(session);
  sessionRef.current = session;

  const fetchStripeDetails = useCallback(async (customerId: string) => {
    if (!sessionRef.current) return;
    setIsDetailsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-subscription-details', {
        headers: { Authorization: `Bearer ${sessionRef.current.access_token}` },
      });

      if (error) {
        throw error;
      }
      
      setStripeDetails(data as StripeDetails);
    } catch (error) {
      console.error('Error fetching Stripe details:', error);
      setStripeDetails(null);
    } finally {
      setIsDetailsLoading(false);
    }
  }, []);

  const hasLoadedRef = useRef(false);

  const loadSubscription = useCallback(async () => {
    try {
      // Only show loading spinner on initial load, not on re-validations
      if (!hasLoadedRef.current) {
        setIsLoading(true);
      }
      setStripeDetails(null);

      if (!user) {
        setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
        setSubscription(null);
        return;
      }

      // Fetch any subscription row (including canceled) to distinguish
      // "no subscription ever" from "expired trial"
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No row — brand new user, trial will be auto-started
          setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
          setSubscription(null);
        } else {
          console.error('Error fetching subscription:', error);
          setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
          setSubscription(null);
        }
      } else if (data) {
        setSubscription({ ...data, status: data.status as 'active' | 'canceled' | 'past_due' | 'trialing' });

        // Only grant professional access for active/trialing subscriptions
        if (data.status === 'active' || data.status === 'trialing') {
          setTier(SUBSCRIPTION_TIERS.professional as SubscriptionTier);
          if (data.stripe_customer_id) {
            fetchStripeDetails(data.stripe_customer_id);
          }
        } else {
          setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
        }
      } else {
        setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
        setSubscription(null);
      }
    } catch (error) {
      console.error('Unexpected error in loadSubscription:', error);
      setTier(SUBSCRIPTION_TIERS.free as SubscriptionTier);
      setSubscription(null);
    } finally {
      setIsLoading(false);
      hasLoadedRef.current = true;
    }
  }, [user, fetchStripeDetails]);

  useEffect(() => {
    loadSubscription();

    const subscriptionChannel = supabase
      .channel('subscription-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          console.log('Subscription changed, reloading:', payload);
          loadSubscription();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscriptionChannel);
    };
  }, [user, loadSubscription]);

  // Compute trial info from subscription
  const trialInfo = useMemo((): TrialInfo | null => {
    if (!subscription || subscription.status !== 'trialing') {
      return null;
    }
    
    const trialEndsAt = (subscription as any).trial_ends_at;
    if (!trialEndsAt) return null;
    
    const endsAt = new Date(trialEndsAt);
    const now = new Date();
    const daysRemaining = Math.max(0, Math.ceil((endsAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    
    return {
      isTrialing: true,
      daysRemaining,
      trialEndsAt,
      aiRequestsUsed: (subscription as any).trial_ai_requests_used || 0,
      aiRequestsLimit: TRIAL_CONFIG.aiRequestLimit,
    };
  }, [subscription]);

  const isTrialing = subscription?.status === 'trialing';

  return {
    subscription,
    stripeDetails,
    tier,
    isLoading: isLoading || isDetailsLoading,
    // Trial-specific fields
    isTrialing,
    trialInfo,
  };
} 